import { animate, state, style, transition, trigger } from '@angular/animations';
import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { CommentInput } from '@hub4b/models/server/comment-input';
import { CommentState } from '@hub4b/models/server/enums/comment-state';
import { ViewType } from '@hub4b/models/server/enums/view-type';
import { Folder } from '@hub4b/models/server/folder';
import { AvmDateAndTimePipe } from '@hub4b/pipes/avm-date-and-time.pipe';
import { SpinnerService } from '@hub4b/services/core/spinner.service';
import { FolderCommentsServiceProvider } from '@hub4b/services/providers/folder-comments-service.provider';
import { NgxSpinnerModule } from 'ngx-spinner';
import { Subject, takeUntil } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

@Component({
    selector: 'app-expanded-comment-cell',
    templateUrl: './expanded-comment-cell.component.html',
    styleUrls: ['./expanded-comment-cell.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('void', style({ height: '0px', minHeight: '0' })),
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('150ms')),
        ]),
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgClass, NgxSpinnerModule, AvmDateAndTimePipe]
})
export class ExpandedCommentCellComponent {
    public CommentState = CommentState;

    /**
     * Utilisé pour gérer la désincription des observables lorsque le composant est détruit.
     */
    protected componentDestroyed$: Subject<void> = new Subject<void>();

    public sortedComments: CommentInput[];

    /**
     * Instance du dossier affiché sur cette ligne.
     */
    @Input()
    public row: Folder;

    /**
     * Type de vue.
     */
    @Input()
    public viewType: ViewType;

    /**
     * Valeur indiquant si la ligne est dépliée.
     */
    private _isExpanded: boolean = false;

    /**
     * Obtient une valeur indiquant si la ligne est dépliée.
     */
    get isExpanded(): boolean {
        return this._isExpanded;
    }

    /**
     * Spécifie une valeur indiquant si la ligne est dépliée.
     */
    @Input()
    set isExpanded(isExpanded: boolean) {
        this._isExpanded = isExpanded;
        if (isExpanded) {
            this.spinner.show(this.spinnerName);
            this.folderCommentsServiceProvider
                .getService(this.viewType)
                .getComment(this.row.viewId, this.row.id)
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe((document) => {
                    this.folderCommentsServiceProvider
                        .getService(this.viewType)
                        .getCommentContent(this.row.viewId, this.row.id, document.id, document.attachments[0].id)
                        .pipe(takeUntil(this.componentDestroyed$))
                        .subscribe((comments) => {
                            this.sortedComments = this.sort(comments);
                            // Stoke le texte du commentaire dans une propriété indiquant au navigateur que le texte est SAFE (du fait que l'on affiche le texte dans l'attribut InnerHtml pour qu'il soit interprété au format HTML).
                            this.sortedComments.forEach((x) => (x.safeText = this.sanitizer.bypassSecurityTrustHtml(x.text)));
                            this.cdr.markForCheck();
                            this.spinner.hide(this.spinnerName);
                        });
                });
        }
    }

    public spinnerName = uuidv4();

    constructor(
        private folderCommentsServiceProvider: FolderCommentsServiceProvider,
        private spinner: SpinnerService,
        private cdr: ChangeDetectorRef,
        private sanitizer: DomSanitizer
    ) {}

    sort(comments: CommentInput[]) {
        return comments.sort((a, b) => {
            var comparaison = new Date(b.date).getTime() - new Date(a.date).getTime();

            if (comparaison === 0) {
                comparaison = b.iteration - a.iteration;
            }

            return comparaison;
        });
    }
}
